import React, { FC, useEffect, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Avatar,
  Chip,
  CircularProgress,
  alpha,
} from '@mui/material';
import {
  Close as CloseIcon,
  Search as SearchIcon,
  AccountTree,
  ArrowForward,
  NavigateBefore,
  NavigateNext,
  CalendarToday,
  LocationOn,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import clientService from 'src/clientService';
import { Project } from 'src/api/Api';

interface ProjectSelectionModalProps {
  open: boolean;
  onClose: () => void;
}

const PAGE_SIZE = 10;

const ProjectSelectionModal: FC<ProjectSelectionModalProps> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [page, setPage] = useState<number>(1);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');

  const fetchProjectsForPage = useCallback(async (targetPage: number) => {
    setIsLoading(true);
    try {
      const skip = (targetPage - 1) * PAGE_SIZE;
      // Request PAGE_SIZE + 1 to reliably determine if a subsequent page exists without an extra query
      const response = await clientService.api.readProjectsApiV1ProjectsGet({
        skip,
        limit: PAGE_SIZE + 1,
      });

      const data = response.data || [];
      const hasNext = data.length > PAGE_SIZE;
      const pageData = hasNext ? data.slice(0, PAGE_SIZE) : data;

      setProjects(pageData);
      setHasMore(hasNext);
      setPage(targetPage);
    } catch (error) {
      console.error('Failed to fetch projects page:', error);
      setProjects([]);
      setHasMore(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      setSearchTerm('');
      void fetchProjectsForPage(1);
    }
  }, [open, fetchProjectsForPage]);

  const handleSelectProject = (projectId: string) => {
    onClose();
    navigate(`/project/${projectId}/overview`);
  };

  const handleNextPage = () => {
    if (!isLoading && hasMore) {
      setSearchTerm('');
      void fetchProjectsForPage(page + 1);
    }
  };

  const handlePrevPage = () => {
    if (!isLoading && page > 1) {
      setSearchTerm('');
      void fetchProjectsForPage(page - 1);
    }
  };

  const filteredProjects = projects.filter((p) => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return (
      p.project_name?.toLowerCase().includes(term) ||
      p.project_type?.toLowerCase().includes(term) ||
      p.country?.toLowerCase().includes(term) ||
      p.project_developer?.toLowerCase().includes(term)
    );
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0F0F0F',
          backgroundImage: 'none',
          border: '1px solid #222',
          borderRadius: '16px',
          color: '#fff',
          overflow: 'hidden',
          boxShadow: '0 24px 48px rgba(0, 0, 0, 0.8), 0 0 1px 1px rgba(255, 255, 255, 0.05)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          px: 3,
          py: 2.5,
          borderBottom: '1px solid #1F1F1F',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Avatar
              variant="rounded"
              sx={{
                width: 32,
                height: 32,
                bgcolor: alpha('#10B981', 0.15),
                color: '#10B981',
              }}
            >
              <AccountTree sx={{ fontSize: 18 }} />
            </Avatar>
            <Typography sx={{ fontSize: '18px', fontWeight: 700, color: '#fff' }}>
              Select Project
            </Typography>
          </Box>
          <Typography sx={{ mt: 0.5, fontSize: '12px', color: '#888' }}>
            Choose a project to enter workspace
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{
            color: '#777',
            '&:hover': { color: '#fff', bgcolor: 'rgba(255, 255, 255, 0.05)' },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* Search Bar */}
      <Box sx={{ px: 3, pt: 2.5, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Filter projects by name, type, country..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#666', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#151515',
              color: '#fff',
              borderRadius: '10px',
              '& fieldset': { borderColor: '#262626' },
              '&:hover fieldset': { borderColor: '#3a3a3a' },
              '&.Mui-focused fieldset': { borderColor: '#10B981' },
            },
          }}
        />
      </Box>

      {/* Content */}
      <DialogContent sx={{ px: 3, py: 2, minHeight: 420 }}>
        {isLoading ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={360} gap={2}>
            <CircularProgress size={36} sx={{ color: '#10B981' }} />
            <Typography sx={{ fontSize: '13px', color: '#888' }}>
              Loading projects from backend...
            </Typography>
          </Box>
        ) : filteredProjects.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height={360} gap={1}>
            <Typography sx={{ fontSize: '14px', color: '#999', fontWeight: 600 }}>
              {searchTerm ? 'No matching projects found' : 'No projects available on this page'}
            </Typography>
            <Typography sx={{ fontSize: '12px', color: '#666' }}>
              {searchTerm ? 'Try a different search keyword' : 'Check back later or return to earlier pages'}
            </Typography>
            {page > 1 && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => fetchProjectsForPage(1)}
                sx={{
                  mt: 1.5,
                  color: '#10B981',
                  borderColor: '#10B981',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontSize: '12px',
                  '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.1)', borderColor: '#10B981' },
                }}
              >
                Back to First Page
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {filteredProjects.map((project) => (
              <Box
                key={project.project_id}
                onClick={() => handleSelectProject(project.project_id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  borderRadius: '12px',
                  backgroundColor: '#141414',
                  border: '1px solid #222',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: 'rgba(16, 185, 129, 0.06)',
                    borderColor: 'rgba(16, 185, 129, 0.45)',
                    transform: 'translateX(4px)',
                    '& .project-arrow': {
                      transform: 'translateX(3px)',
                      color: '#10B981',
                    },
                    '& .project-title-text': {
                      color: '#10B981',
                    },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0, flex: 1 }}>
                  <Avatar
                    variant="rounded"
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: alpha('#10B981', 0.1),
                      color: '#10B981',
                      border: '1px solid rgba(16, 185, 129, 0.2)',
                      flexShrink: 0,
                    }}
                  >
                    <AccountTree sx={{ fontSize: 20 }} />
                  </Avatar>

                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
                      <Typography
                        className="project-title-text"
                        sx={{
                          fontSize: '14px',
                          fontWeight: 700,
                          color: '#fff',
                          transition: 'color 0.15s ease',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: '400px',
                        }}
                      >
                        {project.project_name}
                      </Typography>

                      <Chip
                        label={project.project_type || 'Conservation'}
                        size="small"
                        sx={{
                          height: '20px',
                          fontSize: '11px',
                          fontWeight: 600,
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          color: '#10B981',
                          border: '1px solid rgba(16, 185, 129, 0.25)',
                        }}
                      />

                      {project.project_status && (
                        <Chip
                          label={project.project_status}
                          size="small"
                          sx={{
                            height: '20px',
                            fontSize: '11px',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            color: '#888',
                            border: '1px solid #282828',
                          }}
                        />
                      )}
                    </Box>

                    <Box display="flex" alignItems="center" gap={2} mt={0.5} flexWrap="wrap">
                      {project.country && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <LocationOn sx={{ fontSize: 13, color: '#666' }} />
                          <Typography sx={{ fontSize: '11px', color: '#777' }}>
                            {project.country}
                          </Typography>
                        </Box>
                      )}

                      {project.project_developer && (
                        <Typography sx={{ fontSize: '11px', color: '#666' }}>
                          Dev: <span style={{ color: '#888' }}>{project.project_developer}</span>
                        </Typography>
                      )}

                      {project.project_start_date && (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <CalendarToday sx={{ fontSize: 12, color: '#666' }} />
                          <Typography sx={{ fontSize: '11px', color: '#777' }}>
                            {project.project_start_date}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </Box>

                <IconButton
                  className="project-arrow"
                  sx={{
                    color: '#666',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    ml: 1,
                  }}
                >
                  <ArrowForward sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>

      {/* Footer Pagination */}
      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: '1px solid #1F1F1F',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: '#0B0B0B',
        }}
      >
        <Typography sx={{ fontSize: '12px', color: '#777' }}>
          Page <span style={{ color: '#fff', fontWeight: 700 }}>{page}</span>
          {projects.length > 0 && ` (${projects.length} Projects loaded)`}
        </Typography>

        <Box display="flex" alignItems="center" gap={1.5}>
          <Button
            size="small"
            variant="outlined"
            onClick={handlePrevPage}
            disabled={page <= 1 || isLoading}
            startIcon={<NavigateBefore />}
            sx={{
              color: '#fff',
              borderColor: '#333',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 600,
              '&:hover': {
                borderColor: '#10B981',
                bgcolor: 'rgba(16, 185, 129, 0.08)',
              },
              '&.Mui-disabled': {
                borderColor: '#222',
                color: '#444',
              },
            }}
          >
            Previous
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={handleNextPage}
            disabled={!hasMore || isLoading}
            endIcon={<NavigateNext />}
            sx={{
              bgcolor: '#10B981',
              color: '#07110d',
              borderRadius: '8px',
              textTransform: 'none',
              fontSize: '12px',
              fontWeight: 700,
              '&:hover': { bgcolor: '#059669' },
              '&.Mui-disabled': {
                bgcolor: '#222',
                color: '#555',
              },
            }}
          >
            Next Page
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ProjectSelectionModal;
